'use client'

import { cn } from '@/lib/utils'
import { MARK_PATHS } from './crucible-mark-paths'

/**
 * The rounded-square app-icon form of the mark: "Crucible-icon-square" in the
 * Figma brand file. Geometry is the file's own: an 800-unit tile with a
 * 180-unit corner radius and the mark inset so it stands 714 units tall.
 * The mark paths are the same data the bare mark uses, scaled into the tile.
 *
 * Use it where a tile used to sit (the app header, the mobile header); use
 * `CrucibleMark` where the mark stands on its own.
 */

const TILE = 800
const RADIUS = 180
/** Mark box inside the tile, from the Figma frame: x 132, y 43, height 714. */
const MARK_X = 132
const MARK_Y = 43
const MARK_SCALE = 714 / 828.0432739257812

const AMBER = '#D96B26'
const INK = '#0D0D0D'
const PAPER = '#FFFFFF'

export type BadgeVariant =
  /** Amber tile, white mark. The general-purpose badge; reads on both themes. */
  | 'amber'
  /** Near-black tile, amber mark. The app-icon form. */
  | 'dark'
  /** White tile, amber mark. */
  | 'light'

const FILLS: Record<BadgeVariant, { tile: string; mark: string }> = {
  amber: { tile: AMBER, mark: PAPER },
  dark: { tile: INK, mark: AMBER },
  light: { tile: PAPER, mark: AMBER },
}

interface CrucibleBadgeProps {
  variant?: BadgeVariant
  /** Any CSS colour (a `var(--persona-*)` included) for the tile, over the variant's. */
  tileFill?: string
  /** Any CSS colour for the mark, over the variant's. */
  markFill?: string
  className?: string
  /** Accessible name. Pass an empty string to mark the image decorative. */
  title?: string
  style?: React.CSSProperties
}

export function CrucibleBadge({
  variant = 'amber',
  tileFill,
  markFill,
  className,
  title = 'Crucible',
  style,
}: CrucibleBadgeProps) {
  const fill = { tile: tileFill ?? FILLS[variant].tile, mark: markFill ?? FILLS[variant].mark }
  const a11y = title ? ({ role: 'img', 'aria-label': title } as const) : ({ 'aria-hidden': true } as const)
  return (
    <svg
      viewBox={`0 0 ${TILE} ${TILE}`}
      style={style}
      className={cn('block h-8 w-auto shrink-0', className)}
      {...a11y}
    >
      <rect width={TILE} height={TILE} rx={RADIUS} fill={fill.tile} />
      <g fill={fill.mark} transform={`translate(${MARK_X} ${MARK_Y}) scale(${MARK_SCALE})`}>
        <path d={MARK_PATHS.bowlLeft} />
        <path d={MARK_PATHS.bowlRight} />
        <path d={MARK_PATHS.rimLeft} />
        <path d={MARK_PATHS.rimRight} />
        <path d={MARK_PATHS.flame} />
      </g>
    </svg>
  )
}
